import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import JSZip from 'jszip';
import { User, DocumentType, Document } from '../types';
import { api } from '../services/api';
import { auth, storage } from '../services/firebase';
import { analyzeDocument } from '../services/ai-service';
import { sanitizeForStorage } from '../utils/textUtils';
import {
    FileText,
    Plus,
    Trash2,
    Edit,
    CheckCircle,
    XCircle,
    Search,
    Filter,
    Download,
    Eye,
    Settings,
    Loader2,
    UploadCloud,
    Send,
    MessageSquare,
    Upload,
    Bot,
    Bell,
    ChevronLeft,
    ChevronRight,
    Clock,
    Sparkles, // New Icon
    AlertTriangle
} from 'lucide-react';

import { formatCurrency, formatDate } from '../utils/formatters';
import DocumentActionModal from './DocumentActionModal';
import { DocumentNotesModal } from './DocumentNotesModal';
import { BulkUploadWizard } from './BulkUploadWizard';
import { LocalizedDatePicker } from './ui/LocalizedDatePicker';

interface DocumentsManagementViewProps {
    user: User; // Agency User
    initialFilter?: 'unread_notes';
}

export const DocumentsManagementView: React.FC<DocumentsManagementViewProps> = ({ user, initialFilter }) => {
    const { t } = useTranslation(['documents', 'bi', 'common']);
    const [activeTab, setActiveTab] = useState<'new_review' | 'all_docs' | 'types' | 'bulk_upload'>('new_review');
    const [agencyName, setAgencyName] = useState('');

    useEffect(() => {
        if (initialFilter === 'unread_notes') {
            setActiveTab('all_docs');
        }
    }, [initialFilter]);

    // Fetch Agency Name correctly (Owner vs Member)
    useEffect(() => {
        const fetchAgencyName = async () => {
            if (user.role === 'agency') {
                setAgencyName(user.profile?.companyName || user.config?.companyName || 'Agency');
            } else if (user.role === 'agency_member' && user.agencyId) {
                console.log("[DEBUG] Fetching Agency Owner Profile. AgencyID:", user.agencyId);
                try {
                    // Use Backend Endpoint to bypass Firestore Rules
                    const profile = await api.getPublicProfile(user.agencyId);
                    console.log("[DEBUG] Agency Owner Fetched:", profile);
                    const name = profile.companyName || 'Agency';
                    console.log("[DEBUG] Setting Agency Name to:", name);
                    setAgencyName(name);
                } catch (e: any) {
                    console.error("[DEBUG] Failed to fetch agency name. Error:", e);
                    setAgencyName('Agency');
                }
            } else {
                setAgencyName('Agency');
            }
        };
        fetchAgencyName();
    }, [user]);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
                    <FileText size={28} className="text-blue-600" /> {t('main_title')}
                </h1>

                <div className="flex gap-4">
                    <button
                        onClick={() => setActiveTab('bulk_upload')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'bulk_upload' ? 'bg-black text-white shadow-lg' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                    >
                        <UploadCloud size={18} /> {t('tabs.bulk_upload')}
                    </button>

                    <div className="flex bg-slate-100 p-1 rounded-xl">
                        <button
                            onClick={() => { setActiveTab('new_review'); }}
                            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'new_review' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            {t('tabs.review_new')}
                        </button>
                        <button
                            onClick={() => setActiveTab('all_docs')}
                            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'all_docs' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            {t('tabs.all_docs')}
                        </button>
                        <button
                            onClick={() => setActiveTab('types')}
                            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'types' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            {t('tabs.types')}
                        </button>
                    </div>
                </div>
            </div>

            {activeTab === 'types' ? (
                <DocumentTypesManager user={user} />
            ) : activeTab === 'bulk_upload' ? (
                <BulkUploadWizard
                    user={user}
                    onClose={() => setActiveTab('all_docs')}
                    onSuccess={() => setActiveTab('all_docs')}
                />
            ) : (
                <DocumentsReviewer
                    user={user}
                    viewMode={activeTab === 'new_review' ? 'new' : 'all'}
                    initialFilter={initialFilter}
                    agencyName={agencyName}
                />
            )}
        </div>
    );
};

// --- SUB-COMPONENT: Document Types Manager ---
const DocumentTypesManager = ({ user }: { user: User }) => {
    const { t } = useTranslation('documents');
    const [types, setTypes] = useState<DocumentType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingType, setEditingType] = useState<DocumentType | null>(null);

    // Default Generation State
    const [isGeneratingDefaults, setIsGeneratingDefaults] = useState(false);
    const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);

    // Result Modal State
    const [generationResult, setGenerationResult] = useState<{ show: boolean, created: number, skipped: number, success: boolean, errorMessage?: string }>({
        show: false, created: 0, skipped: 0, success: true
    });

    const handleGenerateDefaults = async () => {
        setShowGenerateConfirm(false);
        setIsGeneratingDefaults(true);
        try {
            const agencyId = user.role === 'agency' ? user.id : user.agencyId;
            if (!agencyId) return;

            const result = await api.createDefaultDocumentTypes(agencyId, user.plan);
            if (result.created === 0 && result.skipped > 0) {
                // All existed
                setGenerationResult({ show: true, created: 0, skipped: result.skipped, success: true });
            } else {
                setGenerationResult({ show: true, created: result.created, skipped: result.skipped, success: true });
                loadTypes();
            }
        } catch (error: any) {
            console.error(error);
            setGenerationResult({ show: true, created: 0, skipped: 0, success: false, errorMessage: error.message });
        } finally {
            setIsGeneratingDefaults(false);
        }
    };



    useEffect(() => {
        loadTypes();
    }, []);

    const loadTypes = async () => {
        setIsLoading(true);
        try {
            const agencyId = user.role === 'agency' ? user.id : user.agencyId;
            if (agencyId) {
                const data = await api.getDocumentTypes(agencyId);
                setTypes(data);
            }
        } catch (error) {
            console.error(error);
            alert("Failed to load document types");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (confirm("Are you sure? This will not delete existing documents of this type, but clients won't be able to select it anymore.")) {
            await api.deleteDocumentType(id);
            loadTypes();
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <div>
                    <div>
                        <h2 className="text-lg font-bold text-slate-900">{t('types_manager.title')}</h2>
                        <p className="text-slate-500 text-sm">{t('types_manager.subtitle')}</p>
                    </div>
                    <div className="flex flex-col gap-3 mt-4">
                        <button
                            onClick={() => { setEditingType(null); setShowModal(true); }}
                            className="bg-black text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-800 transition flex items-center justify-center gap-2 w-full"
                        >
                            <Plus size={16} /> {t('types_manager.new_type')}
                        </button>
                        {/* NEW BUTTON: Generate Defaults */}
                        {user.role === 'agency' && (
                            <button
                                onClick={() => setShowGenerateConfirm(true)}
                                disabled={isGeneratingDefaults}
                                className="text-violet-600 bg-violet-50 hover:bg-violet-100 px-4 py-2 rounded-lg text-sm font-bold transition flex items-center justify-center gap-2 w-full"
                            >
                                {isGeneratingDefaults ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
                                {t('types_manager.generate_defaults')}
                            </button>
                        )}
                    </div>

                </div>

                {isLoading ? (
                    <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-slate-500">
                                <tr>
                                    <th className="px-6 py-4">{t('table.name')}</th>
                                    <th className="px-6 py-4">{t('table.category')}</th>
                                    <th className="px-6 py-4">{t('table.visibility')}</th>
                                    <th className="px-6 py-4">{t('types_manager.check_client_upload')}</th>
                                    <th className="px-6 py-4">AI OCR</th>
                                    <th className="px-6 py-4">{t('table.required')}</th>
                                    <th className="px-6 py-4 text-right">{t('table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {types.length === 0 ? (
                                    <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-400">No document types defined.</td></tr>
                                ) : (
                                    types.map(docType => (
                                        <tr key={docType.id} className="hover:bg-slate-50 group">
                                            <td className="px-6 py-4 font-medium text-slate-900">
                                                <div>{docType.nameTranslations?.[user.language as 'en' | 'el'] || docType.nameTranslations?.en || docType.name}</div>
                                                <div className="text-xs text-slate-400 font-mono">{docType.name}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs uppercase font-bold tracking-wider">{docType.category}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {docType.isVisibleToClient ? (
                                                    <span className="text-green-600 flex items-center gap-1 text-xs font-bold"><Eye size={14} /> {t('status.visible')}</span>
                                                ) : (
                                                    <span className="text-slate-400 text-xs font-bold">{t('status.hidden')}</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {docType.clientCanUpload ? (
                                                    <span className="text-blue-600 flex items-center gap-1 text-xs font-bold"><Upload size={14} /> Yes</span>
                                                ) : (
                                                    <span className="text-slate-400 text-xs">-</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {docType.aiOcrEnabled ? (
                                                    <span className="text-purple-600 flex items-center gap-1 text-xs font-bold"><Bot size={14} /> ON</span>
                                                ) : (
                                                    <span className="text-slate-400 text-xs">-</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {docType.isRequired && <span className="bg-red-50 text-red-600 px-2 py-1 rounded-full text-xs font-bold">{t('table.required')}</span>}
                                            </td>
                                            <td className="px-6 py-4 text-right flex justify-end gap-2">
                                                <button onClick={() => { setEditingType(docType); setShowModal(true); }} className="p-2 hover:bg-slate-200 rounded-lg text-slate-500"><Edit size={16} /></button>
                                                <button onClick={() => handleDelete(docType.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500"><Trash2 size={16} /></button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {showModal && (
                    <DocumentTypeModal
                        user={user}
                        type={editingType}
                        onClose={() => setShowModal(false)}
                        onSave={() => { setShowModal(false); loadTypes(); }}
                    />
                )}
            </div>

            {/* Confirmation Modal for Defaults */}
            {showGenerateConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white w-full max-w-md rounded-2xl p-6 shadow-2xl animate-fadeIn">
                        <div className="flex items-center gap-3 text-violet-600 mb-4">
                            <div className="bg-violet-100 p-3 rounded-full">
                                <Sparkles size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900">{t('types_manager.generate_modal_title')}</h3>
                        </div>

                        <p className="text-slate-600 mb-6" dangerouslySetInnerHTML={{ __html: t('types_manager.generate_modal_desc') }} />

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowGenerateConfirm(false)}
                                className="px-4 py-2 font-bold text-slate-500 hover:bg-slate-50 rounded-lg"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleGenerateDefaults}
                                className="bg-violet-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-violet-700 transition shadow-lg shadow-violet-200"
                            >
                                {t('types_manager.generate_btn')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Result Modal */}
            {generationResult.show && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white w-full max-w-md rounded-2xl p-6 shadow-2xl animate-fadeIn">
                        <div className={`flex items-center gap-3 ${generationResult.success ? 'text-green-600' : 'text-red-600'} mb-4`}>
                            <div className={`${generationResult.success ? 'bg-green-100' : 'bg-red-100'} p-3 rounded-full`}>
                                {generationResult.success ? <CheckCircle size={24} /> : <AlertTriangle size={24} />}
                            </div>
                            <h3 className="text-xl font-bold text-slate-900">
                                {generationResult.success ? t('types_manager.generate_success_title') : 'Error'}
                            </h3>
                        </div>

                        <p className="text-slate-600 mb-6">
                            {generationResult.success ? (
                                generationResult.created === 0 && generationResult.skipped > 0
                                    ? t('types_manager.generate_already_exists')
                                    : t('types_manager.generate_success_desc', { created: generationResult.created, skipped: generationResult.skipped })
                            ) : (
                                generationResult.errorMessage
                            )}
                        </p>

                        <div className="flex justify-end">
                            <button
                                onClick={() => setGenerationResult({ ...generationResult, show: false })}
                                className="bg-slate-900 text-white px-6 py-2 rounded-lg font-bold hover:bg-slate-800 transition"
                            >
                                {t('actions.close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>

    );
};

// --- SUB-COMPONENT: Document Type Modal ---

const DocumentTypeModal = ({ user, type, onClose, onSave }: { user: User, type: DocumentType | null, onClose: () => void, onSave: () => void }) => {
    const { t } = useTranslation('documents');
    const [data, setData] = useState<Partial<DocumentType>>(type || {
        name: '',
        nameTranslations: { en: '', el: '' },
        description: '',
        category: 'other',
        allowedFileTypes: ['pdf', 'jpg', 'png'],
        isRequired: false,
        isVisibleToClient: true,
        clientCanUpload: false,
        aiOcrEnabled: false,
        active: true,
        agencyId: user.role === 'agency' ? user.id : user.agencyId
    });
    const [isSaving, setIsSaving] = useState(false);

    // Initialize translations if editing existing type without them
    useEffect(() => {
        if (type && !data.nameTranslations) {
            setData(prev => ({
                ...prev,
                nameTranslations: { en: type.name, el: '' } // Default EN to current name
            }));
        }
    }, [type]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            if (type) {
                await api.updateDocumentType(type.id, data);
            } else {
                await api.createDocumentType(data as any);
            }
            onSave();
        } catch (e: any) {
            alert(e.message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-lg rounded-2xl p-6 shadow-2xl">
                <h2 className="text-xl font-bold mb-4">{type ? t('types_manager.modal_title_edit') : t('types_manager.modal_title_new')}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="block text-sm font-bold text-slate-700 mb-1">{t('types_manager.label_name')} (Default/Internal)</label>
                            <input type="text" required value={data.name} onChange={e => setData({ ...data, name: e.target.value })} className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black" />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">English Name</label>
                            <input
                                type="text"
                                value={data.nameTranslations?.en || ''}
                                onChange={e => setData({ ...data, nameTranslations: { ...data.nameTranslations, en: e.target.value } })}
                                className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black"
                                placeholder="e.g. Balance Sheet"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">Greek Name</label>
                            <input
                                type="text"
                                value={data.nameTranslations?.el || ''}
                                onChange={e => setData({ ...data, nameTranslations: { ...data.nameTranslations, el: e.target.value } })}
                                className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black"
                                placeholder="π.χ. Ισολογισμός"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">{t('types_manager.label_category')}</label>
                        <select value={data.category} onChange={e => setData({ ...data, category: e.target.value as any })} className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black bg-white">
                            <option value="financial">Financial</option>
                            <option value="legal">Legal</option>
                            <option value="tax">Tax</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    <div className="flex gap-4 pt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={data.isRequired} onChange={e => setData({ ...data, isRequired: e.target.checked })} className="w-4 h-4 accent-black" />
                            <span className="text-sm font-medium">{t('types_manager.check_required')}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={data.isVisibleToClient} onChange={e => setData({ ...data, isVisibleToClient: e.target.checked })} className="w-4 h-4 accent-black" />
                            <span className="text-sm font-medium">{t('types_manager.check_visible')}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={data.clientCanUpload} onChange={e => setData({ ...data, clientCanUpload: e.target.checked })} className="w-4 h-4 accent-black" />
                            <span className="text-sm font-medium">{t('types_manager.check_client_upload', 'Client can Upload')}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={data.aiOcrEnabled} onChange={e => setData({ ...data, aiOcrEnabled: e.target.checked })} className="w-4 h-4 accent-black" />
                            <span className="text-sm font-medium">{t('types_manager.check_ai_ocr', 'AI OCR SCAN')}</span>
                        </label>
                    </div>

                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 font-bold text-slate-500 hover:bg-slate-50 rounded-lg">Cancel</button>
                        <button type="submit" disabled={isSaving} className="bg-black text-white px-6 py-2 rounded-lg font-bold hover:opacity-80 transition flex items-center gap-2">
                            {isSaving && <Loader2 className="animate-spin" size={16} />} Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- SUB-COMPONENT: Documents Reviewer ---
const DocumentsReviewer = ({ user, viewMode, initialFilter, agencyName }: { user: User, viewMode: 'new' | 'all', initialFilter?: 'unread_notes', agencyName: string }) => {
    const { t } = useTranslation('documents');
    const [docs, setDocs] = useState<Document[]>([]);
    const [clients, setClients] = useState<User[]>([]);
    const [types, setTypes] = useState<DocumentType[]>([]);

    // Pagination State
    const [lastVisible, setLastVisible] = useState<any>(null);
    const [cursorStack, setCursorStack] = useState<any[]>([null]);
    const [hasMore, setHasMore] = useState(false);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [limitCount] = useState(50); // Limit: 50

    // Bulk Selection State
    const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());

    // Filter State
    const [filterType, setFilterType] = useState('');
    const [filterClient, setFilterClient] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);
    const [filterUnread, setFilterUnread] = useState(initialFilter === 'unread_notes');

    useEffect(() => {
        if (initialFilter === 'unread_notes') setFilterUnread(true);
    }, [initialFilter]);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingDoc, setEditingDoc] = useState<Document | null>(null);
    const [showUploadModal, setShowUploadModal] = useState(false);

    // Modal Config
    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        type: 'success' | 'error' | 'delete' | 'confirm';
        title: string;
        message: string;
        docId?: string;
        docStoragePath?: string;
        onConfirmAction?: () => void;
    }>({
        isOpen: false,
        type: 'success',
        title: '',
        message: ''
    });
    const [isDeleting, setIsDeleting] = useState(false);

    // Notes State
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [notesDoc, setNotesDoc] = useState<Document | null>(null);

    const handleNotesClick = (doc: Document) => {
        setNotesDoc(doc);
        setShowNotesModal(true);

        if (doc.metadata?.hasUnreadNotesForAgency) {
            api.markDocumentNotesAsRead(doc.id, true).then(() => {
                setDocs(prev => prev.map(d => d.id === doc.id ? {
                    ...d,
                    metadata: { ...d.metadata, hasUnreadNotesForAgency: false }
                } : d));
            });
        }
    };

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: 'date' | 'type' | 'status'; direction: 'asc' | 'desc' }>({ key: 'date', direction: 'desc' });

    const requestSort = (key: 'date' | 'type' | 'status') => {
        if (key !== 'date') {
            alert("Sorting by other fields is not supported in this view yet.");
            return;
        }
    };

    const getSortIcon = (key: 'date' | 'type' | 'status') => {
        if (key !== 'date') return null;
        return '↓';
    };

    // Handle Selection
    const toggleSelection = (id: string) => {
        const newSet = new Set(selectedDocIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedDocIds(newSet);
    };

    const toggleSelectAll = () => {
        if (selectedDocIds.size === docs.length && docs.length > 0) {
            setSelectedDocIds(new Set());
        } else {
            setSelectedDocIds(new Set(docs.map(d => d.id)));
        }
    };

    const fetchPaginatedData = async (cursor: any = null) => {
        setIsLoading(true);
        setError(null);
        try {
            const agencyId = user.role === 'agency' ? user.id : user.agencyId;
            if (!agencyId) return;

            // Fetch dependencies if missing
            // Crucial: Use a local variable to ensure we have the list for the logic immediately below
            let currentClients = clients;

            if (clients.length === 0) {
                const agencyClients = await api.getAgencyClients(agencyId, user.role === 'agency_member' ? user.id : undefined);
                setClients(agencyClients);
                currentClients = agencyClients; // Update local reference for this run
            }
            if (types.length === 0) {
                const typeList = await api.getDocumentTypes(agencyId);
                setTypes(typeList);
            }

            // Prepare Filters
            const filters: any = {};

            // Base filters per view mode
            if (viewMode === 'new') {
                filters.status = 'pending';
                filters.uploadedByRole = 'client';
            } else {
                if (filterType) filters.typeId = filterType;
                if (filterStatus) filters.status = filterStatus;
                if (filterDateFrom) filters.dateFrom = new Date(filterDateFrom);
                if (filterDateTo) {
                    const d = new Date(filterDateTo);
                    d.setHours(23, 59, 59, 999);
                    filters.dateTo = d;
                }
            }

            // Handle Client Filters & Permissions (Global Logic)
            const isAgencyMember = user.role === 'agency_member';
            let targetClientIds: string[] | undefined = undefined;

            // 1. If Agency Member, they are RESTRICTED to their assigned clients.
            // We must explicitly pass these IDs to satisfy Firestore Security Rules (which require clientId check).
            if (isAgencyMember) {
                // Use 'currentClients' which is guaranteed to be loaded
                targetClientIds = currentClients.map(c => c.id);
            }

            // 2. Text Search overrides/refines this list (matches logic in both views)
            if (filterClient) {
                const query = filterClient.toLowerCase();
                const matchedClients = currentClients.filter(c => {
                    const name = (c.profile?.companyName || c.config?.companyName || '').toLowerCase();
                    const vat = (c.profile?.vatNumber || '').toLowerCase();
                    const email = c.email.toLowerCase();
                    return name.includes(query) || vat.includes(query) || email.includes(query);
                });

                if (matchedClients.length === 0) {
                    filters.clientId = '___NO_MATCH___';
                    targetClientIds = undefined; // Force empty result
                } else if (matchedClients.length === 1) {
                    filters.clientId = matchedClients[0].id;
                    targetClientIds = undefined; // Use single ID optimization
                } else {
                    targetClientIds = matchedClients.map(c => c.id);
                }
            }

            // 3. Apply targetClientIds if set (from Member Restriction OR Search)
            // Note: This logic now runs for 'new' view as well, securing the query.
            if (targetClientIds && !filters.clientId) {
                // API Limit for 'in' is 30.
                // If member has > 30 clients and no search, we unfortunately currently truncate to 30.
                // TODO: Better solution is denormalizing 'assignedAgents' array to Documents or 'Select Client' compulsory for large lists.
                /*
                if (targetClientIds.length > 30) {
                   console.warn("User has more than 30 clients. Query limited to first 30.");
                }
                */
                filters.clientIds = targetClientIds;
            }

            // Unread filter check (placeholder)
            // Unread filter check
            if (viewMode !== 'new' && filterUnread) {
                filters.hasUnreadNotes = true;
            }

            const res = await api.getPaginatedDocuments(agencyId, limitCount, cursor, filters);
            setDocs(res.data);
            setLastVisible(res.lastVisible);
            setHasMore(res.hasMore);
            if (res.totalCount !== undefined) setTotalCount(res.totalCount);
        } catch (error: any) {
            console.error(error);
            // Handle Index Building Error
            if (error?.message?.includes('index') || error?.code === 'failed-precondition') {
                setError("System is currently optimizing the database indexes for this view. This may take a few minutes. Please check back shortly.");
            } else {
                setError("Failed to load documents. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const loadData = () => {
        // Refresh current page
        const currentCursor = cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : null;
        fetchPaginatedData(currentCursor);
    };

    // Payment Status Logic
    const [updatingStatusIds, setUpdatingStatusIds] = useState<Set<string>>(new Set());

    const handleTogglePaymentStatus = async (doc: Document) => {
        if (updatingStatusIds.has(doc.id)) return;

        const currentStatus = doc.paymentStatus ?? 'pending';
        const newStatus = currentStatus === 'paid' ? 'pending' : 'paid';

        // Optimistic Update
        setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, paymentStatus: newStatus } : d));

        try {
            setUpdatingStatusIds(prev => new Set(prev).add(doc.id));
            await api.updateDocumentPaymentStatus(doc.id, newStatus);
        } catch (e) {
            console.error("Status Update Failed", e);
            alert("Failed to update status");
            // Revert optimistic update
            setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, paymentStatus: currentStatus } : d));
        } finally {
            setUpdatingStatusIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(doc.id);
                return newSet;
            });
        }
    };

    // Filter Change Effect
    useEffect(() => {
        const timer = setTimeout(() => {
            setCursorStack([null]);
            setLastVisible(null);
            fetchPaginatedData(null);
        }, 500);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.id, viewMode, filterType, filterStatus, filterDateFrom, filterDateTo, agencyName, filterClient, filterUnread]);

    // Clear Selection Effect
    useEffect(() => { setSelectedDocIds(new Set()); }, [viewMode, filterType, filterStatus, filterDateFrom, filterDateTo]);

    const handleNextPage = () => {
        if (!hasMore || !lastVisible) return;
        setCursorStack(prev => [...prev, lastVisible]);
        fetchPaginatedData(lastVisible);
    };

    const handlePrevPage = () => {
        if (cursorStack.length <= 1) return;
        const newStack = [...cursorStack];
        newStack.pop(); // Remove current page start
        const prevCursor = newStack[newStack.length - 1];
        setCursorStack(newStack);
        fetchPaginatedData(prevCursor);
    };

    const handleDeleteClick = (doc: Document) => {
        setModalConfig({
            isOpen: true,
            type: 'delete',
            title: t('messages.delete_title'),
            message: t('messages.delete_confirm', { title: doc.title }) + ' ' + t('messages.delete_desc'),
            docId: doc.id,
            docStoragePath: doc.storagePath
        });
    };

    const confirmAction = async () => {
        if (modalConfig.onConfirmAction) {
            await modalConfig.onConfirmAction();
            return;
        }
        if (!modalConfig.docId) return;
        setIsDeleting(true);
        try {
            await api.deleteDocument(modalConfig.docId, modalConfig.docStoragePath);
            setModalConfig(prev => ({ ...prev, isOpen: false }));
            loadData();
        } catch (error) {
            console.error("Delete failed", error);
            alert("Failed to delete document");
        } finally {
            setIsDeleting(false);
        }
    };

    // Check Status Handlers
    const handleBulkStatus = async (status: 'approved' | 'rejected') => {
        if (selectedDocIds.size === 0) return;
        setModalConfig({
            isOpen: true,
            type: 'confirm',
            title: status === 'approved' ? t('actions.approve_bulk_title') : t('actions.reject_bulk_title'),
            message: status === 'approved' ? t('actions.approve_bulk_message', { count: selectedDocIds.size }) : t('actions.reject_bulk_message', { count: selectedDocIds.size }),
            onConfirmAction: async () => {
                setModalConfig(prev => ({ ...prev, isOpen: false }));
                setIsLoading(true);
                try {
                    for (const docId of Array.from(selectedDocIds)) await handleStatus(docId, status, true);
                    setSelectedDocIds(new Set());
                    loadData();
                } catch (e) { console.error(e); alert("Failed."); } finally { setIsLoading(false); }
            }
        });
    };

    const handleBulkDelete = async () => {
        if (selectedDocIds.size === 0) return;
        setModalConfig({
            isOpen: true,
            type: 'confirm',
            title: t('actions.delete_bulk_title'),
            message: t('actions.delete_bulk_message', { count: selectedDocIds.size }),
            onConfirmAction: async () => {
                setModalConfig(prev => ({ ...prev, isOpen: false }));
                setIsLoading(true);
                try {
                    const docsToDelete = docs.filter(d => selectedDocIds.has(d.id));
                    for (const doc of docsToDelete) await api.deleteDocument(doc.id, doc.storagePath);
                    setSelectedDocIds(new Set());
                    loadData();
                } catch (e) { console.error(e); alert("Failed."); } finally { setIsLoading(false); }
            }
        });
    };



    const handleStatus = async (id: string, status: 'approved' | 'rejected', skipReload = false) => {
        try {
            let moveContext = undefined;
            if (status === 'approved') {
                const doc = docs.find(d => d.id === id);
                if (doc) {
                    const client = clients.find(c => c.id === doc.clientId);
                    if (client) {
                        moveContext = {
                            agencyName: agencyName, // from state
                            clientName: client.profile?.companyName || client.config?.companyName || 'Client'
                        };
                    }
                }
            }

            await api.updateDocumentStatus(id, status, moveContext);
            if (!skipReload) loadData();
        } catch (e) {
            console.error(e);
            if (!skipReload) alert('Failed to update status');
            throw e; // Re-throw for bulk handler
        }
    };

    const handleBulkDownload = async () => {
        if (selectedDocIds.size === 0) return;
        setIsDownloading(true);
        try {
            const zip = new JSZip();
            // Fetch full document details for all selected IDs (handling pagination)
            const docsToDownload = await api.getDocumentsByIds(Array.from(selectedDocIds));

            if (docsToDownload.length === 0) {
                alert("No documents found for selection.");
                return;
            }

            // Map to track duplicate filenames
            const filenameCounts: { [key: string]: number } = {};

            const processFile = async (doc: Document) => {
                try {
                    // Try fetching via proxy if local, else direct
                    let url = doc.fileUrl;
                    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                        url = url.replace('https://firebasestorage.googleapis.com', '/firebase-storage-proxy');
                    }

                    const response = await fetch(url);
                    if (!response.ok) throw new Error('Fetch failed');
                    const blob = await response.blob();

                    // Handle duplicate filenames
                    let filename = doc.title;
                    const getExtensionFromMime = (mime: string): string => {
                        switch (mime) {
                            case 'application/pdf': return 'pdf';
                            case 'image/jpeg': return 'jpg';
                            case 'image/png': return 'png';
                            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': return 'xlsx';
                            case 'application/vnd.ms-excel': return 'xls';
                            case 'application/msword': return 'doc';
                            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': return 'docx';
                            default: return mime.split('/')[1] || 'bin';
                        }
                    };

                    const ext = getExtensionFromMime(doc.fileType);

                    const originalFilename = doc.storagePath.split('/').pop() || doc.title;
                    let finalName = originalFilename;

                    // Fix: If filename ends with underscore + extension (e.g. _pdf), replace with dot
                    // Or if it doesn't end with correct extension, append it
                    const suffix = `.${ext}`;
                    const underscoreSuffix = `_${ext}`;

                    if (finalName.endsWith(underscoreSuffix)) {
                        finalName = finalName.substring(0, finalName.length - underscoreSuffix.length) + suffix;
                    } else if (!finalName.toLowerCase().endsWith(suffix)) {
                        finalName = `${finalName}${suffix}`;
                    }

                    if (filenameCounts[finalName]) {
                        const count = filenameCounts[finalName]++;
                        const parts = finalName.split('.');
                        if (parts.length > 1) {
                            const e = parts.pop();
                            finalName = `${parts.join('.')}_(${count}).${e}`;
                        } else {
                            finalName = `${finalName}_(${count})`;
                        }
                    } else {
                        filenameCounts[finalName] = 1;
                    }

                    zip.file(finalName, blob);
                } catch (e) {
                    console.error(`Failed to download ${doc.title}`, e);
                    // Continue with others
                }
            };

            await Promise.all(docsToDownload.map(processFile));

            const content = await zip.generateAsync({ type: "blob" });
            const url = URL.createObjectURL(content);
            const a = document.createElement("a");
            a.href = url;
            a.download = `documents_archive_${new Date().toISOString().split('T')[0]}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
            alert("Failed to create zip archive.");
        } finally {
            setIsDownloading(false);
        }
    };

    const handleResendEmail = (doc: Document) => {
        setModalConfig({
            isOpen: true,
            type: 'confirm',
            title: t('actions.resend_email_modal.title'),
            message: t('actions.resend_email_modal.message', { title: doc.title }),
            onConfirmAction: async () => {
                try {
                    const agencyName = user.profile?.companyName || user.config?.companyName || 'Agency';
                    await api.sendDocumentNotification({
                        clientId: doc.clientId,
                        docTitle: doc.title,
                        docType: doc.typeName,
                        agencyName: agencyName
                    });
                    setModalConfig({
                        isOpen: true,
                        type: 'success',
                        title: t('actions.resend_email_modal.success_title'),
                        message: t('actions.resend_email_modal.success_message'),
                        onConfirmAction: undefined
                    });
                } catch (error) {
                    console.error(error);
                    setModalConfig({
                        isOpen: true,
                        type: 'error',
                        title: t('actions.resend_email_modal.error_title'),
                        message: t('actions.resend_email_modal.error_message'),
                        onConfirmAction: undefined
                    });
                }
            }
        });
    };

    const handleResendPush = (doc: Document) => {
        setModalConfig({
            isOpen: true,
            type: 'confirm',
            title: "Send Push Notification",
            message: `Send a push notification to the client for document "${doc.title}"?`,
            onConfirmAction: async () => {
                try {
                    await api.notifyClient(doc.id, doc.clientId, "New Document", `You have a new document from ${agencyName}`);
                    setModalConfig({
                        isOpen: true,
                        type: 'success',
                        title: "Success",
                        message: "Push notification sent successfully",
                        onConfirmAction: undefined
                    });
                } catch (error) {
                    console.error(error);
                    setModalConfig({
                        isOpen: true,
                        type: 'error',
                        title: "Error",
                        message: "Failed to send notification",
                        onConfirmAction: undefined
                    });
                }
            }
        });
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                <div>
                    <h2 className="text-lg font-bold text-slate-900">{viewMode === 'new' ? t('tabs.review_new') : t('tabs.all_docs')}</h2>
                    <span className="text-sm text-slate-500">{viewMode === 'new' ? t('messages.subtitle_new') : t('messages.subtitle_all')}</span>
                </div>
                <button
                    onClick={() => setShowUploadModal(true)}
                    className="bg-black text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-800 transition flex items-center gap-2"
                >
                    <Plus size={16} /> {t('upload.agency_title')}
                </button>
            </div>

            {/* Filters Bar */}
            <div className="p-4 border-b border-slate-100 bg-white grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                    <Search size={16} className="absolute left-3 top-3 text-slate-400" />
                    <input
                        type="text"
                        placeholder={t('filter.client_placeholder')}
                        value={filterClient}
                        onChange={e => setFilterClient(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-black"
                    />
                </div>

                {viewMode === 'all' && (
                    <>
                        <select
                            value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-black"
                        >
                            <option value="">{t('filter.all_types')}</option>
                            {types.map(tOption => (
                                <option key={tOption.id} value={tOption.id}>
                                    {tOption.nameTranslations?.[user.language as 'en' | 'el'] || tOption.nameTranslations?.en || tOption.name}
                                </option>
                            ))}
                        </select>
                        <div className="flex gap-2 w-full">
                            <div className="w-1/2">
                                <LocalizedDatePicker
                                    value={filterDateFrom}
                                    onChange={setFilterDateFrom}
                                    format={user.dateFormat}
                                    placeholder={t('filter.date_from')}
                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-black"
                                />
                            </div>
                            <div className="w-1/2">
                                <LocalizedDatePicker
                                    value={filterDateTo}
                                    onChange={setFilterDateTo}
                                    format={user.dateFormat}
                                    placeholder={t('filter.date_to')}
                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-black"
                                />
                            </div>
                        </div>
                        <select
                            value={filterStatus}
                            onChange={e => setFilterStatus(e.target.value)}
                            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-black"
                        >
                            <option value="">{t('filter.all_statuses')}</option>
                            <option value="pending">{t('status.pending')}</option>
                            <option value="approved">{t('status.approved')}</option>
                            <option value="rejected">{t('status.rejected')}</option>
                        </select>
                    </>
                )}
            </div>

            {/* Bulk Action Bar */}
            {selectedDocIds.size > 0 && (
                <div className="bg-blue-50 px-6 py-3 border-b border-blue-100 flex justify-between items-center animate-fadeIn">
                    <div className="font-bold text-blue-900 text-sm">
                        {selectedDocIds.size} selected
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleBulkStatus('approved')}
                            disabled={isLoading}
                            className="bg-green-600 text-white px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-green-700 transition flex items-center gap-1 shadow-sm"
                        >
                            <CheckCircle size={14} /> {t('actions.approve_bulk_title', 'Approve Selected')}
                        </button>
                        <button
                            onClick={() => handleBulkStatus('rejected')}
                            disabled={isLoading}
                            className="bg-red-500 text-white px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-red-600 transition flex items-center gap-1 shadow-sm"
                        >
                            <XCircle size={14} /> {t('actions.reject_bulk_title', 'Reject Selected')}
                        </button>
                        <button
                            onClick={handleBulkDownload}
                            disabled={isDownloading}
                            className="bg-slate-800 text-white px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-slate-900 transition flex items-center gap-1 shadow-sm disabled:opacity-50"
                        >
                            {isDownloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                            {isDownloading ? t('common.uploading', 'Zipping...') : t('actions.download_selected', 'Download Selected')}
                        </button>
                        <button
                            onClick={handleBulkDelete}
                            disabled={isDeleting}
                            className="bg-white text-red-600 border border-red-200 px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-red-50 transition flex items-center gap-1 shadow-sm"
                        >
                            {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                            {isDeleting ? t('common.deleting', 'Deleting...') : t('actions.delete_selected', 'Delete Selected')}
                        </button>
                    </div>
                </div>
            )}

            {/* Unread Filter Toggle (Added) */}
            {viewMode === 'all' && user.config?.enableDocumentNotes !== false && (
                <div className="px-4 pb-4 bg-white border-b border-slate-100 flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 transition">
                        <input
                            type="checkbox"
                            checked={filterUnread}
                            onChange={(e) => setFilterUnread(e.target.checked)}
                            className="w-4 h-4 accent-red-500 rounded"
                        />
                        <span className={`text-sm font-bold ${filterUnread ? 'text-red-600' : 'text-slate-600'}`}>
                            {t('filter.show_unread_notes')}
                        </span>
                    </label>
                </div>
            )}

            {/* Error Message for Indexing */}
            {error && (
                <div className="mx-4 mb-4 p-4 bg-yellow-50 text-yellow-800 rounded-lg border border-yellow-200 flex items-center gap-3">
                    <Loader2 className="animate-spin text-yellow-600" size={20} />
                    <div>
                        <p className="font-bold">System Optimization in Progress</p>
                        <p className="text-sm">{error}</p>
                    </div>
                </div>
            )}

            {isLoading ? (
                <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="px-6 py-4 w-10">
                                    <input
                                        type="checkbox"
                                        checked={docs.length > 0 && selectedDocIds.size === docs.length}
                                        onChange={toggleSelectAll}
                                        className="w-4 h-4 accent-black rounded cursor-pointer"
                                    />
                                </th>
                                <th className="px-6 py-4">{t('table.title')}</th>
                                <th
                                    className="px-6 py-4 cursor-pointer hover:bg-slate-100 select-none"
                                    onClick={() => requestSort('type')}
                                >
                                    {t('table.type')} {getSortIcon('type')}
                                </th>
                                <th className="px-6 py-4">{t('table.client')}</th>
                                <th
                                    className="px-6 py-4 cursor-pointer hover:bg-slate-100 select-none"
                                    onClick={() => requestSort('date')}
                                >
                                    {t('table.date')} {getSortIcon('date')}
                                </th>
                                <th className="px-6 py-4">{t('table.period')}</th>
                                <th
                                    className="px-6 py-4 cursor-pointer hover:bg-slate-100 select-none"
                                    onClick={() => requestSort('status')}
                                >
                                    {t('table.status')} {getSortIcon('status')}
                                </th>
                                <th className="px-6 py-4 text-center">{t('bi:financial_overview.headers.payment_status')}</th>
                                <th className="px-6 py-4 text-center">{t('table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {docs.length === 0 ? (
                                <tr><td colSpan={12} className="px-6 py-12 text-center align-middle text-slate-400">
                                    {viewMode === 'new' ? t('messages.no_new_docs') : t('messages.no_docs')}
                                </td></tr>
                            ) : (
                                docs.map(doc => (
                                    <tr key={doc.id} className={`hover:bg-slate-50 ${selectedDocIds.has(doc.id) ? 'bg-blue-50' : ''}`}>
                                        <td className="px-6 py-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedDocIds.has(doc.id)}
                                                onChange={() => toggleSelection(doc.id)}
                                                className="w-4 h-4 accent-black rounded cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-6 py-4 font-bold text-slate-900 max-w-xs">
                                            <div className="flex items-center gap-2">
                                                {user.config?.enableDocumentNotes !== false && (
                                                    <button
                                                        onClick={() => handleNotesClick(doc)}
                                                        className="text-slate-300 hover:text-yellow-500 transition relative group"
                                                        title="Notes"
                                                    >
                                                        <MessageSquare
                                                            size={18}
                                                            className={
                                                                doc.metadata?.hasUnreadNotesForAgency
                                                                    ? "text-red-500 fill-red-500 animate-pulse"
                                                                    : doc.metadata?.notes
                                                                        ? "text-yellow-500 fill-yellow-500"
                                                                        : ""
                                                            }
                                                        />
                                                    </button>
                                                )}
                                                <div className="truncate" title={doc.title}>
                                                    <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline">
                                                        <FileText size={16} className="text-blue-500 shrink-0" />
                                                        <span className="truncate">{doc.title}</span>
                                                    </a>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-500">{doc.typeName}</td>
                                        <td className="px-6 py-4">
                                            {(() => {
                                                const client = clients.find(c => c.id === doc.clientId);
                                                if (client) {
                                                    return (
                                                        <div>
                                                            <div className="font-bold text-slate-900">{client.profile?.companyName || client.config.companyName || t('table.unknown_company')}</div>
                                                            <div className="text-xs text-slate-500 font-mono">VAT: {client.profile?.vatNumber || 'N/A'}</div>
                                                        </div>
                                                    );
                                                }
                                                return <span className="text-slate-400">{t('table.unknown_client')} ({doc.clientId.substring(0, 6)}...)</span>;
                                            })()}
                                        </td>
                                        <td className="px-6 py-4 text-slate-500">{formatDate(doc.createdAt, user.dateFormat)}</td>
                                        <td className="px-6 py-4 text-slate-500 text-xs text-nowrap">
                                            {doc.metadata?.periodStart ? (
                                                <div className="flex flex-col font-mono bg-slate-100 px-2 py-1 rounded text-center gap-0.5">
                                                    <span>{formatDate(doc.metadata.periodStart, user.dateFormat)} {t('common:to') || 'to'}</span>
                                                    <span>{formatDate(doc.metadata.periodEnd || '', user.dateFormat)}</span>
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            {doc.status === 'pending' && <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-bold">{t('status.pending')}</span>}
                                            {doc.status === 'approved' && <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-bold">{t('status.approved')}</span>}
                                            {doc.status === 'rejected' && <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-bold">{t('status.rejected')}</span>}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {(() => {
                                                const docType = types.find(t => t.id === doc.typeId);
                                                // Only show payment status if AI OCR is enabled for this document type
                                                if (!docType?.aiOcrEnabled) {
                                                    return <span className="text-slate-400">-</span>;
                                                }

                                                const status = doc.paymentStatus ?? 'pending';
                                                const isPaid = status === 'paid';
                                                return (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleTogglePaymentStatus(doc); }}
                                                        disabled={updatingStatusIds.has(doc.id)}
                                                        className={`
                                                            inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all
                                                            ${isPaid
                                                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                                : 'bg-orange-100 text-orange-700 hover:bg-orange-200'}
                                                            ${updatingStatusIds.has(doc.id) ? 'opacity-50 cursor-wait' : ''}
                                                        `}
                                                    >
                                                        {isPaid ? <CheckCircle size={14} /> : <Clock size={14} />}
                                                        {isPaid ? (t('bi:financial_overview.status.paid') || 'Paid') : (t('bi:financial_overview.status.pending') || 'Pending')}
                                                    </button>
                                                );
                                            })()}
                                        </td>
                                        <td className="px-6 py-4 text-center align-middle">
                                            <div className="flex justify-center gap-2 items-center">
                                                {/* Agency Resend Email (Only for All Documents / Agency Uploads typically, but allowed for all if needed) */}
                                                {viewMode === 'all' && (
                                                    <>
                                                        <button
                                                            onClick={() => handleResendEmail(doc)}
                                                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                                                            title={t('actions.resend_email_modal.title')}
                                                        >
                                                            <Send size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleResendPush(doc)}
                                                            className="p-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 ml-1"
                                                            title="Send Push Notification"
                                                        >
                                                            <Bell size={18} />
                                                        </button>
                                                    </>
                                                )}
                                                <button onClick={() => setEditingDoc(doc)} className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200" title="Edit Details"><Edit size={18} /></button>
                                                {doc.status === 'pending' && (
                                                    <>
                                                        <button onClick={() => handleStatus(doc.id, 'approved')} className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100" title={t('actions.approve')}><CheckCircle size={18} /></button>
                                                        <button onClick={() => handleStatus(doc.id, 'rejected')} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100" title={t('actions.reject')}><XCircle size={18} /></button>
                                                    </>
                                                )}
                                                {/* Agency can always delete */}
                                                <button
                                                    onClick={() => handleDeleteClick(doc)}
                                                    className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 ml-1"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div >
            )}

            {/* Pagination Controls */}
            {!isLoading && docs.length > 0 && (
                <div className="flex justify-between items-center px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-xl">
                    <div className="text-sm text-slate-500 font-medium">
                        Page {cursorStack.length} <span className="mx-2 text-slate-300">|</span> Total: {totalCount}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handlePrevPage}
                            disabled={cursorStack.length <= 1 || isLoading}
                            className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-bold text-slate-600 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 bg-white shadow-sm transition-all"
                        >
                            <ChevronLeft size={16} /> {t('pagination.previous', 'Previous')}
                        </button>
                        <button
                            onClick={handleNextPage}
                            disabled={!hasMore || isLoading}
                            className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-bold text-slate-600 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 bg-white shadow-sm transition-all"
                        >
                            {t('pagination.next', 'Next')} <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
            {
                editingDoc && (
                    <DocumentEditModal
                        user={user}
                        doc={editingDoc}
                        clients={clients}
                        agencyName={agencyName}
                        onClose={() => setEditingDoc(null)}
                        onSave={() => { setEditingDoc(null); loadData(); }}
                    />
                )
            }
            {
                showUploadModal && (
                    <AgencyUploadModal
                        user={user}
                        clients={clients}
                        agencyName={agencyName}
                        onClose={() => setShowUploadModal(false)}
                        onSuccess={() => { setShowUploadModal(false); loadData(); }}
                    />
                )
            }

            <DocumentActionModal
                isOpen={modalConfig.isOpen}
                type={modalConfig.type}
                title={modalConfig.title}
                message={modalConfig.message}
                onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmAction}
                isLoading={isDeleting}
            />

            {notesDoc && (
                <DocumentNotesModal
                    isOpen={showNotesModal}
                    onClose={() => { setShowNotesModal(false); setNotesDoc(null); }}
                    documentId={notesDoc.id}
                    documentTitle={notesDoc.title}
                    currentUser={user}
                    agencyName={agencyName}
                />
            )}
        </div >
    );
};

// --- SUB-COMPONENT: Agency Upload Modal ---

const AgencyUploadModal = ({ user, clients, onClose, onSuccess, agencyName }: { user: User, clients: User[], onClose: () => void, onSuccess: () => void, agencyName: string }) => {
    const { t } = useTranslation('documents');
    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadLabel, setUploadLabel] = useState('');
    const [selectedTypeId, setSelectedTypeId] = useState('');
    const [periodStart, setPeriodStart] = useState('');
    const [periodEnd, setPeriodEnd] = useState('');
    const [types, setTypes] = useState<DocumentType[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    // New Feature State
    const [clientSearch, setClientSearch] = useState('');
    const [sendEmailNotification, setSendEmailNotification] = useState(false);
    const [sendPushNotification, setSendPushNotification] = useState(true);

    useEffect(() => {
        const agencyId = user.role === 'agency' ? user.id : user.agencyId;
        if (agencyId) {
            api.getDocumentTypes(agencyId).then(setTypes);
        }
    }, [user.id, user.role, user.agencyId]);

    // Filter Clients based on search
    const filteredClients = clients.filter(c => {
        const query = clientSearch.toLowerCase();
        const name = (c.profile?.companyName || c.config?.companyName || '').toLowerCase();
        const vat = (c.profile?.vatNumber || '').toLowerCase();
        const email = c.email.toLowerCase();
        return name.includes(query) || vat.includes(query) || email.includes(query);
    });

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
            if (!uploadLabel) {
                setUploadLabel(e.target.files[0].name);
            }
        }
    };

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedFile || !selectedTypeId || !selectedClientId) return;

        setIsUploading(true);
        try {
            const client = clients.find(c => c.id === selectedClientId);
            const typeItem = types.find(t => t.id === selectedTypeId);

            const agencyId = user.role === 'agency' ? user.id : user.agencyId; // Restored

            // Human-readable folder structure
            // Human-readable folder structure
            // Transliterate names to Latin to avoid charset issues
            // Human-readable folder structure
            // Transliterate names to Latin to avoid charset issues
            const agencyNameRaw = agencyName || 'Agency'; // Use passed prop
            const clientNameRaw = client?.profile?.companyName || client?.config?.companyName || client?.email || 'Unknown';
            const typeNameRaw = typeItem?.name || 'Other';

            const agencyFolder = sanitizeForStorage(agencyNameRaw);
            const clientFolder = sanitizeForStorage(clientNameRaw);
            const typeFolder = sanitizeForStorage(typeNameRaw);

            const year = new Date().getFullYear();
            const timestamp = Date.now();
            const filename = `${timestamp}_${sanitizeForStorage(selectedFile.name)}`;

            // Old: documents/AGENCY_ID/CLIENT_ID/YEAR/TYPE/FILE
            // New: documents/AGENCY_NAME/CLIENT_NAME/TYPE_NAME/YEAR/FILE
            const path = `documents/${agencyFolder}/${clientFolder}/${typeFolder}/${year}/${filename}`;

            // 1. Upload File with Metadata
            const metadata = {
                agencyId: agencyId,
                clientId: selectedClientId
            };
            const downloadUrl = await api.uploadDocumentFile(selectedFile, path, metadata);

            // 2. AI Analysis (If Enabled by Client Config AND Document Type)
            let aiAnalysis = undefined;
            // Get Type Settings
            const selectedType = types.find(t => t.id === selectedTypeId);

            console.log("[HandleUpload] Checking AI triggers:", {
                clientAIEnabled: client?.config?.ai_features?.document_ai_enabled,
                typeAIEnabled: selectedType?.aiOcrEnabled,
                typeName: selectedType?.name
            });

            if (client?.config?.ai_features?.document_ai_enabled && selectedType?.aiOcrEnabled) {
                // Use Client's Custom Key or Agency's System Key?
                // For now, we assume the Agency has set a System Setting API Key, 
                // but we'll use a placeholder or check if we can get it from specific config.
                // IMPLEMENTATION NOTE: In a real app, strict backend proxy is required.
                // Here we use the hardcoded key from the user's "mind" (environment variable)
                // or skip if missing.
                console.log("[HandleUpload] AI Analysis Starting.");

                try {
                    // Temporary ID (will be replaced or we use a UUID if we want consistency)
                    const tempId = `doc_${timestamp}`;
                    // API Key param removed
                    const analysis = await analyzeDocument(selectedFile, tempId, selectedClientId);
                    console.log("[HandleUpload] AI Analysis Success:", analysis);
                    aiAnalysis = analysis;
                } catch (aiError) {
                    console.error("AI Analysis Failed (Non-blocking):", aiError);
                    // We continue without AI data
                }
            }

            // 3. Create Document Record
            const newDocId = await api.createDocument({
                typeId: selectedTypeId,
                typeName: typeItem?.name || 'Unknown',
                title: uploadLabel || selectedFile.name,
                agencyId: agencyId, // CORRECTED: Was user.id
                clientId: selectedClientId,
                uploadedBy: user.id,
                uploadedByRole: 'agency', // Important: Mark as agency upload
                fileUrl: downloadUrl,
                storagePath: path,
                fileType: selectedFile.type,
                size: selectedFile.size,
                status: 'approved', // Auto-approve since agency uploaded it
                version: 1,
                metadata: {
                    periodStart: periodStart || undefined,
                    periodEnd: periodEnd || undefined
                },
                ai_analysis: aiAnalysis || null // Add Structured Data (ensure null if undefined)
            });

            // 3. Send Email Notification (if checked)
            if (sendEmailNotification) {
                const docTitle = uploadLabel || selectedFile.name;
                const docTypeName = typeItem?.name || 'Unknown';
                const agencyName = user.profile?.companyName || user.config?.companyName || 'Agency';

                // Fire and forget email notification
                api.sendDocumentNotification({
                    clientId: selectedClientId,
                    docTitle: docTitle,
                    docType: docTypeName,
                    agencyName: agencyName
                }).catch(err => console.error("Failed to send email notification", err));
            }

            // 4. Send Push Notification (if checked)
            if (sendPushNotification) {
                const agencyName = user.profile?.companyName || user.config?.companyName || 'Agency';
                api.notifyClient(
                    newDocId,
                    selectedClientId,
                    "New Document",
                    `You have a new document from ${agencyName}`
                ).catch(err => console.error("Failed to send push notification", err));
            }

            onSuccess();
        } catch (error: any) {
            console.error(error);
            alert(t('messages.upload_failed') + error.message);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-lg rounded-2xl p-6 shadow-2xl overflow-y-auto max-h-[90vh]">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{t('upload.agency_title')}</h2>
                    <button onClick={onClose}><XCircle size={24} className="text-slate-400 hover:text-slate-600" /></button>
                </div>
                <form onSubmit={handleUpload} className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">{t('upload.select_client')}</label>
                        <input
                            type="text"
                            placeholder={t('upload.search_placeholder')}
                            value={clientSearch}
                            onChange={e => setClientSearch(e.target.value)}
                            className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black mb-2 text-sm bg-slate-50"
                        />
                        <select
                            required
                            size={5} // Show multiple items for easier selection after filter
                            value={selectedClientId}
                            onChange={e => setSelectedClientId(e.target.value)}
                            className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black bg-white"
                        >
                            <option value="" disabled>{t('upload.choose_client', { count: filteredClients.length })}</option>
                            {filteredClients.map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.profile?.companyName || c.config.companyName || c.email} ({c.profile?.vatNumber || 'N/A'})
                                </option>
                            ))}
                            {filteredClients.length === 0 && (
                                <option disabled>{t('upload.no_clients')}</option>
                            )}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">{t('upload.doc_type')}</label>
                        <select
                            required
                            value={selectedTypeId}
                            onChange={e => setSelectedTypeId(e.target.value)}
                            className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black bg-white"
                        >
                            <option value="">{t('upload.choose_type')}</option>
                            {types.map(tOption => (
                                <option key={tOption.id} value={tOption.id}>
                                    {tOption.nameTranslations?.[user.language as 'en' | 'el'] || tOption.nameTranslations?.en || tOption.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">{t('upload.label_file')}</label>
                        <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:bg-slate-50 transition cursor-pointer relative">
                            <input
                                type="file"
                                onChange={handleFileSelect}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                required
                            />
                            {selectedFile ? (
                                <div className="text-sm font-bold text-green-600 flex items-center justify-center gap-2">
                                    <CheckCircle size={16} /> {selectedFile.name}
                                </div>
                            ) : (
                                <div className="text-slate-500">
                                    <p className="font-bold text-sm">{t('upload.drag_drop')}</p>
                                    <p className="text-xs mt-1">{t('upload.supported_formats')}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">{t('upload.display_title')}</label>
                        <input
                            type="text"
                            placeholder={t('upload.placeholder_title')}
                            value={uploadLabel}
                            onChange={e => setUploadLabel(e.target.value)}
                            className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">{t('upload.period_start')}</label>
                            <LocalizedDatePicker value={periodStart} onChange={setPeriodStart} format={user.dateFormat} className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black" />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">{t('upload.period_end')}</label>
                            <LocalizedDatePicker value={periodEnd} onChange={setPeriodEnd} format={user.dateFormat} className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black" />
                        </div>
                    </div>



                    <div className="flex items-center gap-2 pt-2">
                        <input
                            type="checkbox"
                            id="sendEmail"
                            checked={sendEmailNotification}
                            onChange={e => setSendEmailNotification(e.target.checked)}
                            className="w-4 h-4 accent-black cursor-pointer"
                        />
                        <label htmlFor="sendEmail" className="text-sm font-bold text-slate-700 cursor-pointer select-none">{t('upload.send_email')}</label>
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                        <input
                            type="checkbox"
                            id="sendPush"
                            checked={sendPushNotification}
                            onChange={(e) => setSendPushNotification(e.target.checked)}
                            className="w-4 h-4 accent-purple-600 cursor-pointer"
                        />
                        <label htmlFor="sendPush" className="text-sm font-bold text-slate-700 cursor-pointer select-none">Send Mobile Push Notification</label>
                    </div>

                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 font-bold text-slate-500 hover:bg-slate-50 rounded-lg">{t('common.cancel')}</button>
                        <button
                            type="submit"
                            disabled={isUploading || !selectedFile || !selectedClientId || !selectedTypeId}
                            className="bg-black text-white px-6 py-2 rounded-lg font-bold hover:opacity-80 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isUploading ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                            {isUploading ? t('common.uploading') : t('common.upload')}
                        </button>
                    </div>
                </form>
            </div >
        </div >
    );
};

// --- SUB-COMPONENT: Document Edit Modal ---

const DocumentEditModal = ({ user, doc, onClose, onSave, clients, agencyName }: { user: User, doc: Document, onClose: () => void, onSave: () => void, clients: User[], agencyName: string }) => {
    const { t } = useTranslation('documents');
    const [title, setTitle] = useState(doc.title);
    const [periodStart, setPeriodStart] = useState(doc.metadata?.periodStart || '');
    const [periodEnd, setPeriodEnd] = useState(doc.metadata?.periodEnd || '');
    const [selectedTypeId, setSelectedTypeId] = useState(doc.typeId || '');
    const [types, setTypes] = useState<DocumentType[]>([]);

    // Fetch types to allow changing type
    useEffect(() => {
        const agencyId = user.role === 'agency' ? user.id : user.agencyId;
        if (agencyId) {
            api.getDocumentTypes(agencyId).then(setTypes);
        }
    }, [user.id, user.role, user.agencyId]);

    const [isSaving, setIsSaving] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const typeItem = types.find(t => t.id === selectedTypeId);
            const typeName = typeItem?.name || doc.typeName || 'Unknown';

            // Calculate new storage path if we have enough info
            let newStoragePath = undefined;
            if (doc.storagePath) {
                // Get original filename from path
                const parts = doc.storagePath.split('/');
                const originalFilename = parts[parts.length - 1];
                const year = new Date().getFullYear(); // Or keep original year? Better keep upload year if available in path? 
                // Let's use current year for new location or try to extract from old path. 
                // If old path is ID based (documents/UID/UID/2026/...), the 3rd index might be year.
                // But for robustness, let's just use current year OR check if we can parse year.
                // Just use current year for simplicity as "File Organization Year".

                // Find Client Name
                const client = clients.find(c => c.id === doc.clientId);
                const clientNameRaw = client?.profile?.companyName || client?.config?.companyName || client?.email || 'Unknown';
                const agencyNameRaw = agencyName || 'Agency'; // Use derived Agency Name from prop

                const agencyFolder = sanitizeForStorage(agencyNameRaw);
                const clientFolder = sanitizeForStorage(clientNameRaw);
                const typeFolder = sanitizeForStorage(typeName);

                // New Path: documents/AGENCY/CLIENT/TYPE/YEAR/FILENAME
                newStoragePath = `documents/${agencyFolder}/${clientFolder}/${typeFolder}/${year}/${originalFilename}`;
            }

            await api.updateDocument(doc.id, {
                title,
                typeId: selectedTypeId,
                typeName,
                metadata: {
                    ...doc.metadata,
                    periodStart: periodStart || undefined,
                    periodEnd: periodEnd || undefined
                },
                newStoragePath // Pass to backend
            });
            onSave();
        } catch (e: any) {
            console.error("Update Error:", e);
            alert(`Update Failed: ${e.message || 'Unknown error'}`);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-lg rounded-2xl p-6 shadow-2xl">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{t('edit.title')}</h2>
                    <button onClick={onClose}><XCircle size={24} className="text-slate-400 hover:text-slate-600" /></button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">{t('edit.doc_title')}</label>
                        <input type="text" required value={title} onChange={e => setTitle(e.target.value)} className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black" />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">{t('edit.doc_type')}</label>
                        <select value={selectedTypeId} onChange={e => setSelectedTypeId(e.target.value)} className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black bg-white">
                            <option value="">{t('edit.unknown_type')}</option>
                            {types.map(tOption => (
                                <option key={tOption.id} value={tOption.id}>{tOption.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">{t('edit.period_start')}</label>
                            <LocalizedDatePicker value={periodStart} onChange={setPeriodStart} format={user.dateFormat} className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black" />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">{t('edit.period_end')}</label>
                            <LocalizedDatePicker value={periodEnd} onChange={setPeriodEnd} format={user.dateFormat} className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-black" />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 font-bold text-slate-500 hover:bg-slate-50 rounded-lg">{t('common.cancel')}</button>
                        <button type="submit" disabled={isSaving} className="bg-black text-white px-6 py-2 rounded-lg font-bold hover:opacity-80 transition flex items-center gap-2">
                            {isSaving && <Loader2 className="animate-spin" size={16} />} {t('common.save_changes')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
